// Der Start: Sprites laden, Sitzung aufbauen, Texturen erzeugen, Spiel starten.
//
// Geladen wird genau eines: Schiff, Brocken und Erzadern als PNG (VIEW-001, Gate
// FG-40). Alles andere — Icons, Nebel, Sterne, Erz-Drops, Partikel und
// saemtlicher Ton — entsteht danach beim Rechnen.
//
// Fehlt eine Datei, ist das **kein** Grund, nicht zu starten: der prozedurale
// Rueckfall in `gfx/textures.ts` fuellt denselben Schluessel, und der Spieler
// bekommt **einen** Hinweis statt zweiundzwanzig.
//
// Der Sitzungsaufbau darf scheitern — dann laeuft Solo. Ein Spiel, das bei
// nicht erreichbarem Server stehen bleibt, hat seine Kernschleife verloren.

import Phaser from 'phaser';

import { bus } from '../core/bus';
import { Session } from '../net/session';
import { ROCK_SHEET_KEY, SPRITE_MANIFEST, VEIN_SHEET_KEY } from '../gfx/sprites';
import { buildAllTextures } from '../gfx/textures';
import { loadMuted, store } from '../state/store';
import { setMuted } from '../audio/synth';

export interface BootData {
  session: Session;
}

export class BootScene extends Phaser.Scene {
  static readonly KEY = 'boot';

  private session!: Session;

  /** Sprites, die nicht geladen werden konnten — gesammelt, nicht einzeln gemeldet. */
  private readonly missing: string[] = [];

  constructor() {
    super(BootScene.KEY);
  }

  init(data: BootData): void {
    this.session = data.session;
  }

  preload(): void {
    // Vor dem Sitzungsaufbau: die Bilder haengen an keiner Tabelle vom Server.
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      this.missing.push(file.key);
    });
    for (const entry of SPRITE_MANIFEST) {
      // `cols` unterscheidet Raster von Einzelbild. Ein Raster als Bild zu laden
      // gaebe eine Textur mit **einem** Frame: die Szene zeichnete dann alle 28
      // Brockenformen als ein 896 Pixel breites Bild, ohne dass etwas scheitert.
      if (entry.cols) {
        this.load.spritesheet(entry.key, entry.path, {
          frameWidth: entry.size,
          frameHeight: entry.size,
        });
      } else {
        this.load.image(entry.key, entry.path);
      }
    }
  }

  create(): void {
    setMuted(loadMuted());

    const label = this.add
      .text(this.scale.width / 2, this.scale.height / 2, 'FAR MINING\nverbinde …', {
        fontFamily: '"Lucida Console", monospace',
        fontSize: '18px',
        color: '#cdc6b8',
        align: 'center',
      })
      .setOrigin(0.5);

    void this.boot(label);
  }

  private async boot(label: Phaser.GameObjects.Text): Promise<void> {
    const info = await this.session.start();

    // Eine Sitzung zaehlen — aber nur solo. Online zaehlt sie der Server beim
    // Verbindungsaufbau; hier mitzuzaehlen ergaebe zwei Sitzungen fuer eine.
    if (info.mode !== 'online') store.count('sessions');

    // Erst **nach** dem Sitzungsaufbau: die Tabellen vom Server bestimmen,
    // wie viele Erz- und Zonenfarben es gibt. Die Schluessel der geladenen
    // Sprites bleiben dabei unangetastet — nur fehlende werden ersetzt.
    const webgl = this.game.renderer.type === Phaser.WEBGL;
    buildAllTextures(this);

    // Die **einzige** Ausnahme vom Pixel-Look: Brocken und Adern werden linear
    // gefiltert. Sie drehen sich frei und in beliebigem Winkel — mit harter
    // Filterung zerfranst bei jeder Drehung die Kante, und das Korn flimmert.
    // Schiff, Icons und HUD bleiben hart (VIEW-001).
    for (const key of [ROCK_SHEET_KEY, VEIN_SHEET_KEY]) {
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    label.destroy();

    if (this.missing.length) {
      bus.emit('toast', {
        text: `${this.missing.length} Sprites fehlen — es wird gezeichnet statt geladen`,
        tone: 'gold',
      });
    }
    if (!webgl) {
      // Die Brocken sind davon nicht mehr betroffen — sie sind Sprites. Was
      // ohne WebGL entfaellt, ist der Zonenverlauf je Bildpunkt im Hintergrund.
      bus.emit('toast', {
        text: 'Kein WebGL — der Zonenverlauf im Hintergrund bleibt grob',
        tone: 'gold',
      });
    }
    bus.emit('net:status', {
      online: info.mode === 'online',
      players: 0,
      field: info.fieldName,
    });
    // Baum, Karte und Planetenoberfläche laufen dauerhaft mit, aber unsichtbar: sie brauchen die
    // Texturen, die es erst jetzt gibt, und ihr Aufbau soll nicht bei jedem
    // Oeffnen des Panels erneut geschehen. Alle drei bekommen ihren zweiten Riegel
    // (`setOpen`) von der Verdrahtung — Unsichtbarkeit schaltet nur das
    // Zeichnen ab, nicht die Zeigerbehandlung (`oberflaeche.md`).
    this.scene.launch('techtree');
    this.scene.launch('map');
    this.scene.launch('planet');
    this.scene.start('space', { session: this.session, info });

    // Einmal beim Start sagen, woran der Spielstand haengt. Kein Dialog: wer
    // gerade anfaengt, hat noch nichts zu verlieren — der Dialog kommt beim
    // ersten gekauften Upgrade (siehe `ui/account.ts`).
    if (this.session.isGuest) {
      bus.emit('toast', {
        text: 'Du spielst als Gast — dein Spielstand hängt an diesem Browser',
        tone: 'gold',
      });
    }
  }
}
